import { Command, Option } from "clipanion";
import { AzureCliCredential } from "@azure/identity";
import { M8tCommand } from "../../lib/m8t-command.js";
import { getAzAccount } from "../../lib/auth.js";
import { awaitDataPlaneReady } from "../../lib/data-plane-ready.js";
import { makeAgentsProbe } from "../../lib/foundry-ready-probe.js";
import { cachingCredential } from "../../lib/caching-credential.js";
import { REQUEST_TIMEOUT_MS } from "../../lib/http.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";

export class FoundryAwaitReadyCommand extends M8tCommand {
  static paths = [["foundry", "await-ready"]];
  static usage = Command.Usage({
    description: "Wait until a freshly-created Foundry project's data plane reliably serves it.",
    details:
      "Probes the project (GET /agents) until it returns 200 on a few consecutive tries, or fails clearly after a bounded budget. A newly-created account can serve intermittent 404 'Project not found' for minutes; run this after 'foundry create' and before deploying agents so the worker phase doesn't catch the unstable window.",
    examples: [["Wait for a project to be ready", "$0 foundry await-ready --endpoint https://acc.services.ai.azure.com/api/projects/m8t"]],
  });

  endpoint = Option.String("--endpoint");
  consecutive = Option.String("--consecutive", "3");
  attempts = Option.String("--attempts", "60");
  interval = Option.String("--interval", "5");
  /** Per-request ceiling, in seconds. Also caps the token subprocess. */
  requestTimeout = Option.String("--request-timeout", String(REQUEST_TIMEOUT_MS / 1000));
  /** WALL-CLOCK ceiling for the whole wait, in seconds — the number a caller can
   *  publish and be held to. See `budgetMs` in lib/data-plane-ready.ts for why a
   *  deadline is used rather than an attempts x interval product. */
  budget = Option.String("--budget", "120");
  subscription = Option.String("--subscription");
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(this.output as "pretty" | "json" | "auto" | undefined, this.context.stdout as NodeJS.WriteStream);
    const endpoint = typeof this.endpoint === "string" ? this.endpoint : undefined;
    if (!endpoint) {
      throw new LocalCliError({ code: "USAGE", message: "--endpoint is required.", hint: "Example: m8t foundry await-ready --endpoint https://<acct>.services.ai.azure.com/api/projects/m8t" });
    }
    // Every numeric flag is validated rather than coerced. `Number("abc")` is NaN,
    // which silently disables a comparison; `--request-timeout 0` would disable BOTH
    // ceilings this command sets while still publishing a budget — restoring the
    // unbounded wait under a number that looks deliberate.
    const positiveInt = (raw: unknown, flag: string, fallback: number): number => {
      if (typeof raw !== "string") return fallback;
      const n = Number(raw);
      if (!Number.isInteger(n) || n <= 0) {
        throw new LocalCliError({
          code: "USAGE",
          message: `${flag} must be a positive whole number of ${flag === "--budget" || flag === "--interval" || flag === "--request-timeout" ? "seconds" : flag === "--consecutive" ? "consecutive successes" : "attempts"}, got ${JSON.stringify(raw)}.`,
          hint: `Example: ${flag} ${fallback.toString()}`,
        });
      }
      return n;
    };
    const consecutive = positiveInt(this.consecutive, "--consecutive", 3);
    const attempts = positiveInt(this.attempts, "--attempts", 60);
    const intervalMs = positiveInt(this.interval, "--interval", 5) * 1000;
    const budgetMs = positiveInt(this.budget, "--budget", 120) * 1000;

    const requestTimeoutMs = positiveInt(this.requestTimeout, "--request-timeout", REQUEST_TIMEOUT_MS / 1000) * 1000;
    // One attempt's worst case: the token subprocess then the request, each capped
    // at requestTimeoutMs below.
    const attemptCeilingMs = requestTimeoutMs * 2;
    // A budget that cannot afford ONE attempt refuses the first one and reports an
    // exhausted wait after zero probes, in milliseconds — a wait that never waited.
    // Reachable by configuration: both knobs are env overrides read in
    // installer/entrypoint.sh (FOUNDRY_READY_BUDGET_S, FOUNDRY_READY_REQ_TIMEOUT).
    if (budgetMs < attemptCeilingMs) {
      throw new LocalCliError({
        code: "USAGE",
        message: `--budget ${(budgetMs / 1000).toString()}s cannot afford a single attempt, which may cost up to ${(attemptCeilingMs / 1000).toString()}s (twice --request-timeout: the token subprocess, then the request).`,
        hint: `Raise --budget to at least ${(attemptCeilingMs / 1000).toString()}, or lower --request-timeout.`,
      });
    }

    await getAzAccount(); // ensures `az login` (throws a clear error otherwise)
    // BOTH HALVES OF AN ATTEMPT ARE BOUNDED HERE, and neither bounds the other.
    //
    //  - processTimeoutInMs caps the `az account get-access-token` SUBPROCESS.
    //    AzureCliCredential leaves it `undefined`, which child_process.exec treats
    //    as no timeout at all. The subprocess runs BEFORE fetch, so a request
    //    timeout can never see it.
    //  - cachingCredential then means only the FIRST probe pays that cost:
    //    AzureCliCredential caches nothing, so an uncached poll spawns one `az`
    //    per attempt.
    //  - the fetch half is capped by requestTimeoutMs, passed into the probe.
    //
    // BE PRECISE ABOUT WHICH NUMBER NEEDS WHICH KNOWLEDGE. The BUDGET is enforced
    // and enumerates nothing — that is what makes it honest, and it is why the old
    // attempts x interval product was not. The RESERVE does enumerate: it is this
    // attempt's two ceilings added up. Under-stating the reserve does not make the
    // wait unbounded; it overruns by the shortfall, which is a bounded error rather
    // than the open-ended one that started this.
    const credential = cachingCredential(new AzureCliCredential({ processTimeoutInMs: requestTimeoutMs }));
    const probe = makeAgentsProbe(credential, endpoint, requestTimeoutMs);
    const onProgress = mode === "pretty" ? (m: string) => this.context.stderr.write(`  ${colors.dim(m)}\n`) : undefined;

    const startedAt = Date.now();
    const { ready, attempts: used, timedOut } = await awaitDataPlaneReady({
      probe, consecutive, attempts, intervalMs, onProgress, budgetMs, attemptCeilingMs,
    });
    if (!ready) {
      // REPORT THE ELAPSED TIME, NOT THE BUDGET. The loop stops as soon as another
      // attempt could not FINISH inside the budget, so it gives up with the reserve
      // still unspent — saying "within its 120s budget" for a wait that ended at
      // 65s is the same species of number-that-is-not-what-happened this command
      // was rewritten to stop publishing.
      const elapsedS = Math.round((Date.now() - startedAt) / 1000);
      throw new LocalCliError({
        code: "FOUNDRY_NOT_READY",
        message: timedOut
          ? `Foundry data plane did not stabilise for ${endpoint} after ${elapsedS.toString()}s and ${used.toString()} probes (budget ${(budgetMs / 1000).toString()}s, less a ${(attemptCeilingMs / 1000).toString()}s reserve for one more attempt).`
          : `Foundry data plane did not stabilise for ${endpoint} within ${attempts.toString()} attempts (${elapsedS.toString()}s).`,
        hint: "The Foundry data plane may be having a transient instability window — wait a few minutes and re-run, or re-run 'm8t bootstrap launch'.",
      });
    }
    if (mode === "json") { this.context.stdout.write(renderJson({ ready: true, attempts: used }) + "\n"); return 0; }
    this.context.stdout.write(`✓ Foundry data plane ready (${used.toString()} probes).\n`);
    return 0;
  }
}
